import React, { useEffect, useState } from "react";
import { ArrowLeftRight, Copy, Check, X, RefreshCw } from "lucide-react";
import { api, money, dateFmt } from "../lib/api.js";
import { Card, Pill, Empty, Notice } from "../components/UI.jsx";
import { useNavigate } from "react-router-dom";
export default function MatchReview() {
  const [rows, setRows] = useState([]),
    [busy, setBusy] = useState(false),
    [msg, setMsg] = useState(""),
    [dismissed, setDismissed] = useState(new Set()),
    [rules, setRules] = useState([]);
  const navigate = useNavigate();
  const load = async () => {
    setBusy(true);
    try {
      const [matches, config] = await Promise.all([
        api("/movement-matches"),
        api("/config"),
      ]);
      setRows(matches);
      setRules(config.rules || []);
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  const key = (x) =>
      `${x.kind}:${x.a.accountId}:${x.a.id}:${x.b.accountId}:${x.b.id}`,
    visible = rows.filter((x) => !dismissed.has(key(x))),
    dismiss = (x) => setDismissed((s) => new Set([...s, key(x)]));
  const dateTime = (movement) =>
    `${dateFmt(movement.date)}${movement.time ? ` · ${String(movement.time).slice(0, 5)}` : ""}`;
  const link = async (x) => {
    await api("/transfers/link", {
      method: "POST",
      body: {
        firstAccountId: x.a.accountId,
        firstId: x.a.id,
        secondAccountId: x.b.accountId,
        secondId: x.b.id,
      },
    });
    setMsg("Existing movements linked. No third movement was created.");
    dismiss(x);
  };
  const duplicate = async (x) => {
    if (
      !confirm(
        "Delete the second movement as a duplicate? The first movement and its classifications will remain.",
      )
    )
      return;
    await api(`/transactions/${x.b.id}?accountId=${x.b.accountId}`, {
      method: "DELETE",
    });
    setMsg("Duplicate removed.");
    dismiss(x);
  };
  const rulePattern = (x) => {
      const description = String(x.a.description || "").trim();
      return description.match(/^([A-Za-z]{2,8})(?=[.\s:/-])/)?.[1] || description;
    },
    hasRule = (x) => {
      const pattern = rulePattern(x).toLowerCase();
      return rules.some((rule) => {
        try {
          const c = typeof rule.conditions_json === "string" ? JSON.parse(rule.conditions_json) : rule.conditions_json || {};
          return String(c.value || c.regex || "").toLowerCase() === pattern;
        } catch { return false; }
      });
    },
    createRule = (x) => {
      const pattern = rulePattern(x);
      navigate(`/rules?new=1&pattern=${encodeURIComponent(pattern)}&kind=${x.kind}&direction=${Number(x.a.amount) >= 0 ? "income" : "expense"}`);
    };
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Duplicates & transfers</h1>
          <p>
            Review local similarity suggestions. A score is evidence, never an
            automatic decision.
          </p>
        </div>
        <button className="secondary" onClick={load}>
          <RefreshCw size={16} />
          Scan again
        </button>
      </div>
      {msg && (
        <Notice tone={/removed|linked/.test(msg) ? "success" : "error"}>
          {msg}
        </Notice>
      )}
      {busy ? (
        <div className="loading">Comparing movements privately…</div>
      ) : !visible.length ? (
        <Card>
          <Empty title="No possible matches" />
        </Card>
      ) : (
        <div className="rule-list">
          {visible.map((x) => (
            <Card className="match-card" key={key(x)}>
              <div className="section-head">
                <div>
                  {x.kind === "transfer" ? <ArrowLeftRight /> : <Copy />}
                  <h2>
                    {x.kind === "transfer"
                      ? "Possible transfer"
                      : "Possible duplicate"}
                  </h2>
                </div>
                <Pill tone={x.score >= 90 ? "green" : "yellow"}>
                  {x.score}% similar
                </Pill>
              </div>
              <div className="two-col match-comparison">
                <div className="match-movement">
                  <div className="match-meta">
                    <small>{dateTime(x.a)}</small>
                    <small>{x.a.account}</small>
                  </div>
                  <b className="match-description">{x.a.description || "No description"}</b>
                  <strong className="match-amount">{money(x.a.amount, x.a.currency)}</strong>
                </div>
                <div className="match-movement">
                  <div className="match-meta">
                    <small>{dateTime(x.b)}</small>
                    <small>{x.b.account}</small>
                  </div>
                  <b className="match-description">{x.b.description || "No description"}</b>
                  <strong className="match-amount">{money(x.b.amount, x.b.currency)}</strong>
                </div>
              </div>
              <p className="muted">Why: {x.reasons.join(" · ")}</p>
              <div className="actions">
                {x.kind === "transfer" ? (
                  <button className="primary" onClick={() => link(x)}>
                    <Check size={15} />
                    Link existing movements
                  </button>
                ) : (
                  <button className="primary" onClick={() => duplicate(x)}>
                    <Check size={15} />
                    Remove duplicate
                  </button>
                )}
                <button className="secondary" onClick={() => dismiss(x)}>
                  <X size={15} />
                  Not a match
                </button>
                <button
                  className="secondary"
                  disabled={hasRule(x)}
                  title={hasRule(x) ? "An equivalent rule already exists." : "Review a new rule based on this movement."}
                  onClick={() => createRule(x)}
                >
                  {hasRule(x) ? "Rule already exists" : "Create rule"}
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
